<template>
  <div id="dividend-modal">
    <v-dialog :value="isModalActive" max-width="800" @input="inputModal">
      <v-card
        id="dividend-modal-card"
        class="pb-1"
        :color="$vuetify.theme.dark ? 'dark_backgroundDarkBlue' : undefined"
      >
        <v-card-title>
          {{
            typeDividendModal ? $t(`views.dividends.dividendModal.title.${typeDividendModal}`) : ''
          }}
        </v-card-title>

        <v-card-text class="dividend-modal-card-text py-0 mb-0 mt-2">
          <ApiErrorAlert ref="apiErrorAlert" :error="errorCode" />

          <v-form>
            <v-row>
              <v-col cols="12" md="12">
                <PositionSearch
                  v-if="isModalActive"
                  v-model="position"
                  :disabled="loading || editing"
                  :type="positionSearchType"
                  showAlertMessage
                  :alertMessage="positionSearchAlertMessage"
                />
              </v-col>

              <template v-if="position">
                <v-col cols="12" md="12" class="mb-0 pb-0">
                  <BrokerAlert
                    :text="$t('views.dividends.dividendModal.needBroker')"
                    marginClass="mt-3 mb-6"
                    :brokerSearchLoading="brokerSearchLoading"
                    :brokerSearchBrokers="brokerSearchBrokers"
                  />
                  <BrokerSearch
                    v-model="dividend.broker"
                    :brokers.sync="brokerSearchBrokers"
                    :isLoading.sync="brokerSearchLoading"
                    :disabled="loading || editing"
                  />
                </v-col>
                <v-col cols="12" md="12" class="mb-2 pb-0">
                  <DatePicker
                    v-model="dividend.date"
                    :label="$t('views.dividends.dividendModal.date')"
                    :labelTime="$t('common.time')"
                    :loading="loading"
                    datePickerClass="mt-0 mb-0"
                    :hourDefault="22"
                  />
                </v-col>
                <v-col cols="12" md="3" :class="{ 'pt-0 mt-2': isSmAndDown }">
                  <v-text-field
                    v-model="dividend.positionNumber"
                    type="number"
                    :label="$t(`views.dividends.dividendModal.position.${position.type}`)"
                    :disabled="loading"
                    hide-details
                    :class="{ 'pt-0 mt-0': isSmAndDown }"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <div class="items-flex align-end">
                    <CurrencySelector
                      v-model="dividend.currency"
                      :disabled="loading"
                      model="dividend"
                      :label="$t('views.dividends.dividendModal.currency')"
                      @input="selectQuantityCurrencyOptions({ notDefaults: true })"
                    />
                    <div class="quantity-currency-info-icon">
                      <InfoIcon
                        :size="15"
                        :text="`${$t('views.dividends.dividendModal.currencyInfo')}`"
                      />
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" md="4">
                  <CurrencyPriceOrTotal
                    v-model="dividend.quantity"
                    :positionNumber="dividend.positionNumber"
                    :currency="dividend.quantityCurrency"
                    :labelPrice="$t(`views.dividends.dividendModal.quantity.${position.type}`)"
                    :disabled="loading"
                  />
                  <InputCurrencySelector
                    v-model="dividend.quantityCurrency"
                    :currencyOptions="quantityCurrencyOptions"
                    :infoText="$t(`views.dividends.dividendModal.quantityCurrencyInfo`)"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <CurrencyInput
                    v-model="dividend.comission"
                    :currency="dividend.comissionCurrency"
                    :label="$t('views.dividends.dividendModal.comission')"
                    :disabled="loading"
                  />
                  <InputCurrencySelector
                    v-model="dividend.comissionCurrency"
                    :currencyOptions="quantityCurrencyOptions"
                    :infoText="$t(`views.dividends.dividendModal.comissionCurrencyInfo`)"
                  />
                </v-col>
                <template v-if="showExchangeRate">
                  <v-col cols="12" md="12" class="my-0 py-0">
                    <v-switch
                      v-model="dividend.customExchangeRate"
                      :disabled="loading"
                      hide-details
                      :class="{ 'pt-0 mt-0': !isSmAndDown, 'mt-5': isSmAndDown }"
                    >
                      <template v-slot:label>
                        <span class="mr-2">
                          {{ $t(`views.dividends.dividendModal.customExchangeRate.switch`) }}
                        </span>
                        <InfoIcon
                          :text="$t(`views.dividends.dividendModal.customExchangeRate.info`)"
                        />
                      </template>
                    </v-switch>
                  </v-col>
                  <v-col v-if="dividend.customExchangeRate" cols="12" md="6" class="my-0 py-0 mt-2">
                    <ExchangeRateInput
                      v-model="dividend.exchangeRate"
                      :exchangeRatePair="exchangeRatePair"
                      :disabled="loading"
                      @rotateExchangeRatePair="rotateExchangeRatePair"
                    />
                  </v-col>
                </template>
                <v-col cols="12" md="12" class="my-0 py-0">
                  <v-switch
                    v-model="dividend.customOriginRetention"
                    :disabled="loading"
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="mr-2">
                        {{ $t(`views.dividends.dividendModal.customOriginRetention.switch`) }}
                      </span>
                      <InfoIcon
                        :text="$t(`views.dividends.dividendModal.customOriginRetention.info`)"
                      />
                    </template>
                  </v-switch>
                </v-col>
                <v-col
                  v-if="dividend.customOriginRetention && positionCountryIsUserCountry"
                  cols="12"
                  md="12"
                  class="my-0 py-0 mt-4"
                >
                  <v-alert
                    :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softInfo'"
                    icon="mdi-information"
                  >
                    {{
                      $t(
                        `views.dividends.dividendModal.customOriginRetention.infoPositionCountryIsUserCountry`
                      )
                    }}
                  </v-alert>
                </v-col>
                <v-col
                  v-if="dividend.customOriginRetention"
                  cols="12"
                  md="6"
                  class="my-0 py-0 mt-2"
                >
                  <CurrencyInput
                    v-model="dividend.originRetention"
                    :currency="dividend.quantityCurrency"
                    :label="$t('views.dividends.dividendModal.customOriginRetention.label')"
                    :disabled="loading"
                  />
                </v-col>
                <v-col
                  v-if="position.customDefaultDividendOriginRetentionPercent"
                  cols="12"
                  md="12"
                  class="my-0 py-0 mt-2"
                >
                  <v-alert
                    :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softInfo'"
                    icon="mdi-information"
                    class="pa-2 alert-return-dividend-percent"
                  >
                    {{ $t('views.dividends.dividendModal.customOriginRetention.warningPosition')
                    }}{{ position.defaultDividendOriginRetentionPercent }}%.
                  </v-alert>
                </v-col>

                <v-col cols="12" md="12" class="my-0 py-0">
                  <v-switch
                    v-model="dividend.customDestinationRetention"
                    :disabled="loading"
                    hide-details
                  >
                    <template v-slot:label>
                      <span class="mr-2">
                        {{ $t(`views.dividends.dividendModal.customDestinationRetention.switch`) }}
                      </span>
                      <InfoIcon
                        :text="$t(`views.dividends.dividendModal.customDestinationRetention.info`)"
                      />
                    </template>
                  </v-switch>
                </v-col>
                <v-col
                  v-if="dividend.customDestinationRetention"
                  cols="12"
                  md="6"
                  class="my-0 py-0 mt-2"
                >
                  <CurrencyInput
                    v-model="dividend.destinationRetentionBaseCurrency"
                    :currency="user.baseCurrency"
                    :label="$t('views.dividends.dividendModal.customDestinationRetention.label')"
                    :disabled="loading"
                  />
                </v-col>

                <template v-if="showDividendCustomReturnPercent">
                  <v-col cols="12" md="12" class="my-0 py-0">
                    <v-switch
                      v-model="dividend.customReturnPercent"
                      :disabled="loading"
                      hide-details
                    >
                      <template v-slot:label>
                        <span class="mr-2">
                          {{ $t(`views.dividends.dividendModal.customReturnPercent.switch`) }}
                        </span>
                        <InfoIcon
                          :text="$t(`views.dividends.dividendModal.customReturnPercent.info`)"
                        />
                      </template>
                    </v-switch>
                  </v-col>
                  <v-col
                    v-if="dividend.customReturnPercent"
                    cols="12"
                    md="3"
                    class="my-0 py-0 mt-2"
                  >
                    <CurrencyInput
                      v-model="dividend.returnPercent"
                      :label="$t('views.dividends.dividendModal.customReturnPercent.label')"
                      currency="%"
                      :disabled="loading"
                    />
                  </v-col>
                  <v-col
                    v-if="position.customDefaultDividendReturnPercent"
                    cols="12"
                    md="12"
                    class="my-0 py-0 mt-2"
                  >
                    <v-alert
                      :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softInfo'"
                      icon="mdi-information"
                      class="pa-2 alert-return-dividend-percent"
                    >
                      {{ $t('views.dividends.dividendModal.customReturnPercent.warningPosition')
                      }}{{ position.defaultDividendReturnPercent }}%.
                    </v-alert>
                  </v-col>
                </template>
                <!-- <template>
                  <v-col cols="12" md="12" class="my-0 mt-2 py-0">
                    <v-switch v-model="dividend.fxActive" :disabled="loading" hide-details>
                      <template v-slot:label>
                        <span class="mr-2">
                          {{ $t(`views.common.fxActive.switch.dividend`) }}
                        </span>
                        <InfoIcon :text="$t(`views.common.fxActive.info`)" />
                      </template>
                    </v-switch>
                  </v-col>
                </template> -->

                <template v-if="dividend.fxActive && showExchangeRate">
                  <v-col cols="12" md="12" class="my-0 py-0">
                    <v-switch v-model="dividend.autoFx" :disabled="loading" hide-details>
                      <template v-slot:label>
                        <span class="mr-2">
                          {{ $t(`views.common.autoFx.switch`) }}
                        </span>
                        <InfoIcon
                          :text="
                            $t(`views.common.autoFx.info1`) +
                            user.baseCurrency +
                            $t(`views.common.autoFx.info2`) +
                            user.baseCurrency +
                            $t(`views.common.autoFx.info3`)
                          "
                        />
                      </template>
                    </v-switch>
                  </v-col>
                </template>

                <v-col cols="12" md="12" class="mt-3">
                  <v-textarea
                    v-model="dividend.description"
                    :label="$t(`views.dividends.dividendModal.description`)"
                    :disabled="loading"
                    background-color="transparent"
                    auto-grow
                    hide-details
                    rows="1"
                    maxlength="500"
                  ></v-textarea>
                </v-col>

                <v-col cols="12" md="12" class="mt-0 pt-0">
                  <v-card
                    class="preview-card px-3 py-1 pt-2 mt-5 mb-0 elevation-0"
                    :color="$vuetify.theme.dark ? 'dark_backgroundSoftBlue' : 'softBackgroundGrey'"
                  >
                    <h1>{{ $t(`views.dividends.dividendModal.previewDividend`) }}</h1>
                    <div v-if="loadingDebouncePreview" class="mb-7">
                      <v-progress-circular size="15" width="1" indeterminate color="softTextGrey" />
                    </div>
                    <div v-else>
                      <div :class="{ 'items-flex': !isMobile }">
                        <div class="items-flex">
                          <span class="mr-1">
                            <b>{{ $t('views.dividends.dividendTable.total') }}</b>
                          </span>
                          <currency
                            v-if="preview.total"
                            :value="preview.total"
                            :currency="dividend.currency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                        <div v-if="!isMobile" class="mx-3">|</div>
                        <div class="items-flex">
                          <span class="mr-1">
                            <b>
                              {{ $t('views.dividends.dividendTable.total') }} ({{
                                user.baseCurrency
                              }})
                            </b>
                          </span>
                          <currency
                            v-if="preview.totalBaseCurrency"
                            :value="preview.totalBaseCurrency"
                            :currency="user.baseCurrency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                        <div v-if="!isMobile" class="mx-3">|</div>
                        <div class="items-flex">
                          <span class="mr-1">
                            <b>
                              {{ $t('views.dividends.dividendTable.originRetention') }} ({{
                                dividend.quantityCurrency
                              }})
                            </b>
                          </span>
                          <currency
                            v-if="preview.originRetention"
                            :value="preview.originRetention"
                            :currency="dividend.quantityCurrency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                      </div>
                      <div :class="{ 'items-flex': !isMobile }">
                        <!-- <div class="items-flex">
                          <span class="mr-1">
                            <b>
                              {{ $t('views.dividends.dividendTable.neto') }} ({{
                                user.baseCurrency
                              }})
                            </b>
                          </span>
                          <currency
                            v-if="preview.netoBaseCurrency"
                            :value="preview.netoBaseCurrency"
                            :currency="user.baseCurrency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                        <div v-if="!isMobile" class="mx-3">|</div> -->
                        <div class="items-flex">
                          <span class="mr-1">
                            <b>
                              {{ $t('views.dividends.dividendTable.destinationRetention') }} ({{
                                user.baseCurrency
                              }})
                            </b>
                          </span>
                          <currency
                            v-if="preview.destinationRetentionBaseCurrency"
                            :value="preview.destinationRetentionBaseCurrency"
                            :currency="user.baseCurrency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                        <div v-if="!isMobile" class="mx-3">|</div>
                        <div class="items-flex">
                          <span class="mr-1">
                            <b>
                              {{ $t('views.dividends.dividendTable.totalNeto') }} ({{
                                user.baseCurrency
                              }})
                            </b>
                          </span>
                          <currency
                            v-if="preview.totalNetoBaseCurrency"
                            :value="preview.totalNetoBaseCurrency"
                            :currency="user.baseCurrency"
                            neverHide
                          />
                          <span v-else>-</span>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </v-col>
              </template>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="mt-3">
          <v-spacer />
          <v-btn
            class="mr-1"
            text
            :disabled="loading"
            :small="$vuetify.breakpoint.smAndDown"
            @click="closeModal"
          >
            {{ $t('views.dividends.dividendModal.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="saveDisabled"
            :small="$vuetify.breakpoint.smAndDown"
            @click="saveDividend"
          >
            {{
              typeDividendModal ? $t(`views.dividends.dividendModal.save.${typeDividendModal}`) : ''
            }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { pick, find, get } from 'lodash'
import { createDividend, editDividend, previewDividend } from '@/lib/api.js'
import { mapState } from 'vuex'
import { getPositionTypes } from '@/lib/positions/positions.js'
import gtag from '@/lib/gtag'
import DatePicker from '@/views/components/core/DatePicker.vue'
import InfoIcon from '@/views/components/common/InfoIcon'
import CurrencyInput from '@/views/components/common/CurrencyInput'
import CurrencyPriceOrTotal from '@/views/components/common/CurrencyPriceOrTotal'
import CurrencySelector from '@/views/components/common/CurrencySelector'
import InputCurrencySelector from '@/views/components/common/InputCurrencySelector'
import ExchangeRateInput from '@/views/components/common/ExchangeRateInput'
import PositionSearch from '@/views/components/common/PositionSearch'
import BrokerSearch from '@/views/components/common/BrokerSearch'
import BrokerAlert from '@/views/components/common/BrokerAlert'
import modalMixin from '@/views/components/core/modalMixin.js'
import Currency from '@/views/components/common/Currency'
import ApiErrorAlert from '@/views/components/common/ApiErrorAlert'

export default {
  components: {
    DatePicker,
    InfoIcon,
    CurrencyInput,
    CurrencyPriceOrTotal,
    CurrencySelector,
    InputCurrencySelector,
    PositionSearch,
    BrokerSearch,
    BrokerAlert,
    ExchangeRateInput,
    Currency,
    ApiErrorAlert,
  },
  mixins: [modalMixin],
  props: {
    editing: {
      type: Boolean,
      default: false,
    },
    duplicating: {
      type: Boolean,
      default: false,
    },
    editItem: {
      type: Object,
      default: () => ({}),
    },
    typeDividendModal: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    dateMenu: false,
    dividend: null,
    loading: false,
    position: null,
    brokerSearchBrokers: [],
    brokerSearchLoading: false,
    isPairRotated: false,
    quantityCurrencyOptions: [],
    preview: {},
    debouncePreviewTimeout: null,
    loadingDebouncePreview: false,
    errorCode: null,
  }),
  computed: {
    ...mapState(['user']),
    isMobile() {
      return this.$vuetify.breakpoint.xs
    },
    isSmAndDown() {
      return this.$vuetify.breakpoint.smAndDown
    },
    positionSearchType() {
      if (this.typeDividendModal === 'coupon')
        return getPositionTypes({ model: 'couponPositionTypes' })
      else return getPositionTypes({ model: 'dividendPositionTypes' })
    },
    positionSearchAlertMessage() {
      if (this.typeDividendModal === 'coupon')
        return this.$t('views.dividends.dividendModal.needCouponPosition')
      else return this.$t('views.dividends.dividendModal.needDividendPosition')
    },
    multiCurrencyBroker() {
      return this.dividend && this.dividend.broker && this.dividend.broker.multiCurrency
    },
    showExchangeRate() {
      return this.dividend.currency !== this.user.baseCurrency
    },
    exchangeRatePair() {
      if (!this.showExchangeRate) return

      if (this.isPairRotated) return `${this.user.baseCurrency}/${this.dividend.currency}`
      return `${this.dividend.currency}/${this.user.baseCurrency}`
    },
    positionCountryIsUserCountry() {
      if (!this.position || !this.user) return false
      return this.position.country === this.user.country
    },
    saveDisabled() {
      return (
        !this.position ||
        !this.dividend.broker ||
        !this.dividend.date ||
        !this.dividend.positionNumber ||
        !this.dividend.quantity ||
        (this.showExchangeRate &&
          this.dividend.customExchangeRate &&
          (!this.dividend.exchangeRate || this.dividend.exchangeRate <= 0)) ||
        (this.dividend.customOriginRetention &&
          !this.dividend.originRetention &&
          this.dividend.originRetention !== 0) ||
        (this.dividend.customDestinationRetention &&
          !this.dividend.destinationRetentionBaseCurrency &&
          this.dividend.destinationRetentionBaseCurrency !== 0) ||
        (this.dividend.customReturnPercent &&
          !this.dividend.returnPercent &&
          this.dividend.returnPercent !== 0)
      )
    },
    showDividendCustomReturnPercent() {
      const dividendReturnPositionTypes = getPositionTypes({ model: 'dividendReturnPositionTypes' })
      return (
        dividendReturnPositionTypes.indexOf(this.position.type) > -1 &&
        ((this.position.country && !this.positionCountryIsUserCountry) ||
          this.dividend.customOriginRetention)
      )
    },
  },
  watch: {
    position: {
      handler() {
        if (!this.position) return

        if (
          this.position.customDefaultDividendReturnPercent &&
          this.position.defaultDividendReturnPercent
        ) {
          this.dividend.returnPercent = this.position.defaultDividendReturnPercent
        }

        const editingOrDuplicating = this.editing || this.duplicating

        this.$set(
          this.dividend,
          'currency',
          editingOrDuplicating ? this.dividend.currency : this.position.currency
        )
        this.selectQuantityCurrencyOptions({ notDefaults: editingOrDuplicating })

        // ONLY EDITING OR DUPLICATING CHANGES
        if (editingOrDuplicating) {
          this.debouncePreviewDividend()
          return
        }

        // SELECT ITS UNIQUE BROKER BY DEFAULT
        if (this.position.brokers) {
          if (this.position.brokers.length === 1)
            this.$set(this.dividend, 'broker', this.position.brokers[0].broker)
          else {
            const activeBrokers = this.position.brokers.filter((elem) => elem.positionNumber > 0)
            if (activeBrokers.length === 1)
              this.$set(this.dividend, 'broker', activeBrokers[0].broker)
          }
        }

        // SELECT POSITION NUMBER BY DEFAULT
        if (!this.dividend.broker || !this.position.brokers || !this.position.brokers.length) return
        const positionBroker = find(
          this.position.brokers,
          (broker) => broker.broker._id === this.dividend.broker._id
        )
        if (positionBroker && positionBroker.positionNumber && positionBroker.positionNumber > 0)
          this.$set(this.dividend, 'positionNumber', positionBroker.positionNumber)

        this.debouncePreviewDividend()
      },
      deep: true,
    },
    editItem: {
      handler() {
        if (this.editing || this.duplicating) {
          this.dividend = JSON.parse(JSON.stringify(this.editItem))
          this.position = JSON.parse(JSON.stringify(this.editItem.position))
        } else {
          this.resetForm()
        }
      },
      immediate: true,
      deep: true,
    },
    dividend: {
      handler() {
        this.debouncePreviewDividend()
      },
      deep: true,
    },
    'dividend.broker'(newBroker, oldBroker) {
      // evito watch falsos, saltan sin que cambie al cambiar el exchangeRate...
      if (JSON.stringify(newBroker) !== JSON.stringify(oldBroker)) {
        if (this.dividend.broker && !this.editing && !this.duplicating) {
          this.selectQuantityCurrencyOptions()
        }
      }
    },
  },
  methods: {
    resetForm() {
      this.$set(this, 'dividend', {
        broker: null,
        comission: 0,
        comissionCurrency: this.user.baseCurrency,
        returnPercent: 15,
        fxActive: true,
      })
      this.position = null
      this.loading = false
      this.dateMenu = false
      this.isPairRotated = false
      this.errorCode = null
    },
    debouncePreviewDividend() {
      if (!this.position) return

      this.loadingDebouncePreview = true
      clearTimeout(this.debouncePreviewTimeout)
      this.debouncePreviewTimeout = setTimeout(async () => {
        const dividend = JSON.parse(JSON.stringify(this.dividend))
        this.prepareDividend({ dividend })
        const preview = await previewDividend({
          position: this.position,
          dividend,
          broker: this.dividend.broker,
        })
        this.preview = preview.data
        this.loadingDebouncePreview = false
      }, 1000)
    },
    prepareDividend({ dividend }) {
      dividend.type = this.typeDividendModal

      if (!dividend.comission) dividend.comission = 0
      else dividend.comission = parseFloat(dividend.comission) // support 00,01 for example

      if (!this.showExchangeRate || !dividend.customExchangeRate || !dividend.exchangeRate) {
        dividend.customExchangeRate = false
        delete dividend.exchangeRate
      } else {
        if (this.isPairRotated) {
          dividend.exchangeRate = 1 / dividend.exchangeRate
        }
      }

      if (!dividend.customOriginRetention) {
        delete dividend.originRetention

        if (this.positionCountryIsUserCountry) {
          dividend.customReturnPercent = false
        }
      }
      if (!dividend.customDestinationRetention) {
        delete dividend.destinationRetentionBaseCurrency
      }

      if (!dividend.customReturnPercent) {
        delete dividend.returnPercent
      }

      if (!dividend.fxActive || !this.showExchangeRate) {
        dividend.autoFx = false
      }

      return dividend
    },
    async saveDividend() {
      this.loading = true

      let dividend = JSON.parse(JSON.stringify(this.dividend))
      dividend = this.prepareDividend({ dividend })

      const attributes = [
        'type',
        'broker',
        'date',
        'positionNumber',
        'quantity',
        'currency',
        'quantityCurrency',
        'comission',
        'comissionCurrency',
        'customExchangeRate',
        'exchangeRate',
        'customOriginRetention',
        'customDestinationRetention',
        'originRetention',
        'destinationRetentionBaseCurrency',
        'customReturnPercent',
        'returnPercent',
        'fxActive',
        'autoFx',
        'description',
      ]
      const editAttributes = [
        'date',
        'positionNumber',
        'quantity',
        'currency',
        'quantityCurrency',
        'comission',
        'comissionCurrency',
        'customExchangeRate',
        'exchangeRate',
        'customOriginRetention',
        'customDestinationRetention',
        'originRetention',
        'destinationRetentionBaseCurrency',
        'customReturnPercent',
        'returnPercent',
        'fxActive',
        'autoFx',
        'description',
      ]

      try {
        if (this.editing) {
          dividend = pick(dividend, editAttributes)
          await editDividend(this.dividend._id, dividend)
        } else {
          dividend = pick(dividend, attributes)
          dividend.broker = dividend.broker._id
          dividend.position = this.position._id
          await createDividend(dividend)
        }
        this.$emit('saved')
        this.closeModal()

        this.$root.$snackbar.success({ text: this.$t('common.saveOk') })

        gtag.pageview({
          pagePath: `/input/dividends/${this.editing ? 'edit' : 'create'}/${
            this.typeDividendModal
          }/success`,
          pageTitle: `input|dividends|${this.editing ? 'edit' : 'create'}|${
            this.typeDividendModal
          }|success`,
          pageContentGroup: 'modal',
        })
      } catch (error) {
        this.errorCode = get(error, 'data.name', null)
        if (!this.errorCode) {
          this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        } else {
          this.$refs.apiErrorAlert.scrollToError()
        }

        gtag.pageview({
          pagePath: `/input/dividends/${this.editing ? 'edit' : 'create'}/${
            this.typeDividendModal
          }/error`,
          pageTitle: `input|dividends|${this.editing ? 'edit' : 'create'}|${
            this.typeDividendModal
          }|error`,
          pageContentGroup: 'modal',
        })
      }
      this.loading = false
    },
    rotateExchangeRatePair() {
      if (this.dividend.exchangeRate) this.dividend.exchangeRate = 1 / this.dividend.exchangeRate
      this.isPairRotated = !this.isPairRotated
    },
    selectQuantityCurrencyOptions({ notDefaults } = {}) {
      if (this.dividend.currency !== this.user.baseCurrency) {
        this.quantityCurrencyOptions = [this.user.baseCurrency, this.dividend.currency]

        if (notDefaults) return

        if (this.multiCurrencyBroker) {
          this.$set(this.dividend, 'quantityCurrency', this.dividend.currency)
          this.$set(this.dividend, 'comissionCurrency', this.dividend.currency)
          this.$set(this.dividend, 'customExchangeRate', false)
          this.$set(this.dividend, 'autoFx', false)
        } else {
          this.$set(this.dividend, 'quantityCurrency', this.user.baseCurrency)
          this.$set(this.dividend, 'comissionCurrency', this.user.baseCurrency)
          this.$set(this.dividend, 'customExchangeRate', !!this.showExchangeRate)
          this.$set(this.dividend, 'autoFx', !!this.showExchangeRate)
        }
      } else {
        this.quantityCurrencyOptions = []

        if (notDefaults) return

        this.$set(this.dividend, 'quantityCurrency', this.dividend.currency)
        this.$set(this.dividend, 'comissionCurrency', this.dividend.currency)
        this.$set(this.dividend, 'customExchangeRate', false)
        this.$set(this.dividend, 'autoFx', false)
      }
    },
  },
}
</script>

<style lang="scss">
#dividend-modal-card {
  .dividend-modal-card-text {
    .alert-return-dividend-percent {
      font-size: 13px;
    }
    .items-flex {
      display: flex;
      &.align-end {
        align-items: flex-end;
      }
    }
    .quantity-currency-info-icon {
      margin: 0px 0px 1px 1px;
    }
  }
  .preview-card {
    font-size: 13px;
    h1 {
      font-size: 13px;
      font-weight: 500;
    }
  }
}
</style>
